// ============================================================================
// FSA NOTE — SHARED ROW BADGES (Chip primitives)
// ============================================================================
// One place for every small pill / chip that decorates a class or line-item
// row in the Note Modal:
//
//   • DriftChip          — amber "⚠ drift X" (reconciliation gap surfaced
//                          inline next to the row's label)
//   • LedgerDriftChip    — red "⚠ ledger drift X" (saved-as-primary saved
//                          value out of sync with live TB)
//   • ReconciledChip     — green "✓ recon" (line items sum to class total)
//   • CustomClassChip    — indigo "+ CUSTOM" (engagement-time custom class)
//   • HiddenInPrintChip  — muted "HIDDEN IN PRINT" (auditor toggled the
//                          class out of the printed PDF)
//   • RollupChip         — small "rollup" hint (class total = Σ line items)
//   • SourceChip         — line-item source descriptor ("Manual" /
//                          "N ledgers")
//
// These are PURE presentation components — no business logic, no
// reconciliation math, no state. The caller decides when each chip is
// appropriate and passes the values; the component handles styling
// uniformly so the standard 2-col pane (MainNotePane) and the multi-col
// pane (MultiColumnClassTable) stay visually consistent.

import React from 'react';

// ---------- shared sizing primitives ---------------------------------------
//
// Every chip in this file extends `baseChipStyle`. Keep the metrics (font
// size, padding, gap, border-radius) here so a future tweak ripples to
// every chip in lock-step.

const baseChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  padding: '1px 6px',
  borderRadius: 999,
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
};

const formatAmount = (v: number) =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---------- DriftChip -------------------------------------------------------
//
// Generic drift indicator for any reconciliation rule (e.g. PPE Net Block
// vs gross_closing - dep_closing). Caller computes the drift amount and
// passes it in; the chip only renders when the caller decides it should
// (i.e. callers gate on driftAmount != null).

interface DriftChipProps {
  /** Signed difference between stored value and formula expectation. */
  amount: number;
  /** Verbose explanation shown as the native title tooltip. */
  tooltip?: string;
  /** Optional override of the "drift" word — e.g. "PY drift" for prior-year. */
  label?: string;
  /** Optional inline-style overrides (e.g. marginLeft when sitting next to another chip). */
  style?: React.CSSProperties;
}

export const DriftChip: React.FC<DriftChipProps> = ({
  amount, tooltip, label = 'drift', style,
}) => (
  <span
    style={{ ...baseChipStyle, ...driftChipStyle, ...style }}
    title={tooltip ?? `Reconciliation drift: ${formatAmount(amount)}`}
  >
    ⚠ {label} {formatAmount(amount)}
  </span>
);

const driftChipStyle: React.CSSProperties = {
  background: '#fef3c7',
  color: '#92400e',
  cursor: 'help',
};

// ---------- LedgerDriftChip ------------------------------------------------
//
// Red variant — used when the SAVED value is out of sync with the live TB
// (FSA-2.2 saved-as-primary). Distinct colour from DriftChip so the auditor
// reads the two as different audit signals at a glance.

interface LedgerDriftChipProps {
  amount: number;
  tooltip?: string;
  style?: React.CSSProperties;
  /**
   * Optional suffix appended inside the chip — used by the line-item
   * variant in MainNotePane to surface orphan ledger counts (e.g.
   * "· 2 orphan"). Kept generic so any future row-context annotation
   * can hang off the same chip without reaching for a custom span.
   */
  suffix?: React.ReactNode;
  /**
   * Optional override of the "ledger drift" word — e.g. "TB drift"
   * when used on Statement screens / Notes-list where the same
   * saved-vs-live-TB signal is more naturally read as a TB-side drift.
   * Falls back to "ledger drift" for the in-Note-Modal call sites
   * that are already wired against the canonical label.
   */
  label?: string;
}

export const LedgerDriftChip: React.FC<LedgerDriftChipProps> = ({
  amount, tooltip, style, suffix, label = 'ledger drift',
}) => (
  <span
    style={{ ...baseChipStyle, ...ledgerDriftChipStyle, ...style }}
    title={tooltip ?? `Saved value out of sync with live TB. Drift: ${formatAmount(amount)}`}
  >
    ⚠ {label} {formatAmount(amount)}
    {suffix != null && <span style={{ marginLeft: 4 }}>{suffix}</span>}
  </span>
);

const ledgerDriftChipStyle: React.CSSProperties = {
  background: '#fee2e2',
  color: '#991b1b',
  cursor: 'help',
};

// ---------- ReconciledChip --------------------------------------------------

interface ReconciledChipProps {
  tooltip?: string;
  style?: React.CSSProperties;
}

export const ReconciledChip: React.FC<ReconciledChipProps> = ({ tooltip, style }) => (
  <span
    style={{ ...baseChipStyle, ...reconciledChipStyle, ...style }}
    title={tooltip ?? 'Line items sum matches class total'}
  >
    ✓ recon
  </span>
);

const reconciledChipStyle: React.CSSProperties = {
  background: '#dcfce7',
  color: '#166534',
};

// ---------- CustomClassChip -------------------------------------------------

interface CustomClassChipProps {
  tooltip?: string;
  style?: React.CSSProperties;
}

export const CustomClassChip: React.FC<CustomClassChipProps> = ({ tooltip, style }) => (
  <span
    style={{ ...baseChipStyle, ...customClassChipStyle, ...style }}
    title={tooltip ?? 'Engagement-time custom class (added by the auditor; not from template)'}
  >
    + CUSTOM
  </span>
);

const customClassChipStyle: React.CSSProperties = {
  background: '#e0e7ff',
  color: '#3730a3',
  textTransform: 'uppercase',
};

// ---------- HiddenInPrintChip -----------------------------------------------

interface HiddenInPrintChipProps {
  tooltip?: string;
  style?: React.CSSProperties;
}

export const HiddenInPrintChip: React.FC<HiddenInPrintChipProps> = ({ tooltip, style }) => (
  <span
    style={{ ...baseChipStyle, ...hiddenInPrintChipStyle, ...style }}
    title={tooltip ?? 'This row is excluded from the printed PDF'}
  >
    HIDDEN IN PRINT
  </span>
);

const hiddenInPrintChipStyle: React.CSSProperties = {
  background: '#fef3c7',
  color: '#92400e',
  textTransform: 'uppercase',
};

// ---------- RollupChip ------------------------------------------------------
//
// Small affordance on a class row that has line items, signalling that the
// class amount is a rollup of its children (not auditor-entered directly).

interface RollupChipProps {
  tooltip?: string;
  style?: React.CSSProperties;
}

export const RollupChip: React.FC<RollupChipProps> = ({ tooltip, style }) => (
  <span
    style={{ ...baseChipStyle, ...rollupChipStyle, ...style }}
    title={tooltip ?? 'Class total = Σ line items'}
  >
    ROLLUP
  </span>
);

const rollupChipStyle: React.CSSProperties = {
  background: '#f1f5f9',
  color: '#475569',
  textTransform: 'uppercase',
};

// ---------- SourceChip ------------------------------------------------------
//
// Line item source descriptor — "Manual" when allocation_mode = 'manual',
// "N ledgers" when allocation_mode = 'ledgers'. Other modes (cross_note,
// pl_flow) can pass a custom label.

interface SourceChipProps {
  label: string;
  tooltip?: string;
  style?: React.CSSProperties;
}

export const SourceChip: React.FC<SourceChipProps> = ({ label, tooltip, style }) => (
  <span
    style={{ ...baseChipStyle, ...sourceChipStyle, ...style }}
    title={tooltip}
  >
    {label}
  </span>
);

const sourceChipStyle: React.CSSProperties = {
  background: '#f1f5f9',
  color: '#475569',
  textTransform: 'none',
  fontWeight: 500,
};
